import React from 'react';
import Tool, { type ToolHandler } from './Tool';
import type { AssistantToolCall } from 'components/framework/models';
import type Agent from '../agents/Agent';
import { jsonFetch } from 'javascripts/fetch';
import { showNoticeMessage, showAlertMessage } from 'javascripts/messages';

async function addWhiteboardToReference(
  toolCall: AssistantToolCall,
  agent: Agent,
  reference: string
) {
  const createResponse = await jsonFetch(`/api/v1/whiteboards`, {
    method: 'POST',
    body: JSON.stringify({
      embeddable_id: reference,
      assistant_tool_call_id: toolCall.id,
    }),
  });

  const whiteboardResponse = (await createResponse.json()) as {
    whiteboard: { id: RecordId };
  };

  const embedResponse = await jsonFetch(
    `/assistant/embed/${reference}/whiteboard`,
    {
      method: 'POST',
      body: JSON.stringify({
        whiteboard_id: whiteboardResponse.whiteboard.id,
      }),
    }
  );

  const { success } = (await embedResponse.json()) as { success: boolean };

  if (success) {
    showNoticeMessage(`Added whiteboard to ${reference}`);
  } else {
    showAlertMessage(`Failed to add whiteboard to ${reference}`);
  }
}

/**
 */
const createWhiteboardToolHandler: ToolHandler = {
  getToolActions(toolCall: AssistantToolCall, agent: Agent): React.ReactNode {
    if (!toolCall.content?.panel?.body) {
      return null;
    }
    const contentType = toolCall.content.panel.contentType;
    if (contentType !== 'application/vnd.aha.whiteboard') {
      return null;
    }

    const menuItems: React.ReactNode[] = [];

    const messageReferences = agent.controller.getReferencesInSentMessages();

    if (agent.contexts.assistedEditor) {
      menuItems.push(
        <aha-menu-item
          key={'add_to_current_document'}
          onClick={() => {
            agent.contexts.assistedEditor?.insertWhiteboard(toolCall.id);
          }}
        >
          Add to current document
        </aha-menu-item>
      );
    } else if (messageReferences.length > 0) {
      messageReferences.forEach(ref => {
        menuItems.push(
          <aha-menu-item
            key={ref}
            onClick={() => {
              void addWhiteboardToReference(toolCall, agent, ref);
            }}
          >
            Add to {ref}
          </aha-menu-item>
        );
      });
    }

    if (menuItems.length > 0) {
      return (
        <aha-menu>
          <aha-button kind='icon' size='mini' slot='control'>
            <i className='fa-regular fa-plus' />
          </aha-button>
          <aha-menu-content>{menuItems}</aha-menu-content>
        </aha-menu>
      );
    }
  },
};

Tool.register('CreateWhiteboardTool', createWhiteboardToolHandler);
